import api from './api';
import { AuthToken, UserProfileResponse } from '../types';

declare global {
  interface Window {
    google?: any;
  }
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  name: string;
  email: string;
  password: string;
}

export interface SignupResponse {
  user_id: string;
  message: string;
}

let googleIdentityScriptPromise: Promise<void> | null = null;

const loadGoogleIdentityScript = async (): Promise<void> => {
  if (window.google?.accounts?.id) {
    return;
  }

  if (!googleIdentityScriptPromise) {
    googleIdentityScriptPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector('script[data-google-identity="true"]') as HTMLScriptElement | null;
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Identity script')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset.googleIdentity = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Identity script'));
      document.head.appendChild(script);
    });
  }

  await googleIdentityScriptPromise;
};

const getGoogleIdToken = async (googleClientId: string): Promise<string> => {
  await loadGoogleIdentityScript();

  const googleApi = window.google?.accounts?.id;
  if (!googleApi) {
    throw new Error('Google sign-in is unavailable right now. Please refresh and try again.');
  }

  return new Promise<string>((resolve, reject) => {
    let settled = false;

    googleApi.initialize({
      client_id: googleClientId,
      callback: (response: { credential?: string }) => {
        if (settled) {
          return;
        }

        if (response?.credential) {
          settled = true;
          resolve(response.credential);
          return;
        }

        settled = true;
        reject(new Error('Google sign-in did not return a credential.'));
      },
      ux_mode: 'popup',
      auto_select: false,
    });

    googleApi.prompt((notification: any) => {
      if (settled) {
        return;
      }

      if (
        notification?.isNotDisplayed?.() ||
        notification?.isSkippedMoment?.() ||
        notification?.isDismissedMoment?.()
      ) {
        settled = true;
        reject(new Error('Google sign-in was cancelled or unavailable.'));
      }
    });
  });
};

export const authService = {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthToken> {
    const response = await api.post<AuthToken>('/auth/login', credentials);
    return response.data;
  },

  /**
   * Register new user
   */
  async signup(data: SignupData): Promise<SignupResponse> {
    const response = await api.post<SignupResponse>('/auth/signup', data);
    return response.data;
  },

  async loginWithGoogle(): Promise<AuthToken> {
    const googleClientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
    if (!googleClientId) {
      throw new Error('Google sign-in is not configured. Add REACT_APP_GOOGLE_CLIENT_ID in frontend .env.');
    }

    const idToken = await getGoogleIdToken(googleClientId);
    const response = await api.post<AuthToken>('/auth/google', { id_token: idToken });
    return response.data;
  },

  async getProfile(historyLimit: number = 20): Promise<UserProfileResponse> {
    const response = await api.get<UserProfileResponse>('/auth/profile', {
      params: { history_limit: historyLimit },
    });
    return response.data;
  },

  /**
   * Store JWT token in localStorage
   */
  storeToken(token: string): void {
    localStorage.setItem('token', token);
    window.dispatchEvent(new Event('ecopredict-auth-token-changed'));
  },

  /**
   * Get JWT token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  },

  /**
   * Remove JWT token from localStorage
   */
  removeToken(): void {
    localStorage.removeItem('token');
    window.dispatchEvent(new Event('ecopredict-auth-token-changed'));
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  },
};
